import { z } from "zod";

export const Cookies = {
  affiliate: "ref",
  callbackUrl: "authjs.callback-url",
  secure: "__Secure-",
  portfolioAssetTxState: (portfolioId: string, assetName: string) =>
    `portfolio-${portfolioId}-asset-${assetName}-state`,
  getSecureKey: (cookie: string) => `${Cookies.secure}${cookie}` as const,

  email: "email",
  theme: "theme",
} as const;

export const Routes = {
  HOME: "/",
  BLOG: "/blog",
  BLOG_ARTICLE: (slug: string) => `${Routes.BLOG}/${slug}` as const,
  AUTHENTICATION: "/authentication",
  AFFILIATE_LINK: (_userId: string, code: string) =>
    `${Routes.HOME}?${Cookies.affiliate}=${code}`,
  AUTHENTICATION_EMAIL: `/authentication/email`,
  AFFILIATE: "/affiliate",
  REGISTER: "/register",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  LEADERBOARD: "/leaderboard",
  FAQ: "/faq",
  ABOUT: "/about",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  REFUND: "/refund",
  DISCLAIMER: "/disclaimer",
  // Settings
  SETTINGS: "/settings",
  SETTINGS_PROFILE: "/settings/profile",
  SETTINGS_NOTIFICATIONS: "/settings/notifications",
  SETTINGS_MONETIZATION: "/settings/monetization",
  SETTINGS_SECURITY: "/settings/security",
  // Portfolio
  PORTFOLIOS: "/portfolios",
  PORTFOLIO: (portfolioId: string) => `/portfolios/${portfolioId}/overview`,
  PORTFOLIO_ASSET_ADD: (portfolioId: string) =>
    `/portfolios/${portfolioId}/assets/add`,
  PORTFOLIO_CREATE: "/portfolios/create",
  PORTFOLIO_EDIT: (portfolioId: string) => `/portfolios/${portfolioId}/edit`,
  PORTFOLIO_EDIT_ASSET: (portfolioId: string, assetName: string) =>
    `/portfolios/${portfolioId}/edit/assets/${assetName}/edit`,
  PORTFOLIO_EDIT_ASSET_TRANSACTIONS: (portfolioId: string, assetName: string) =>
    `/portfolios/${portfolioId}/edit/assets/${assetName}/transactions`,
  PORTFOLIO_DELETE: (portfolioId: string) =>
    `/portfolios/${portfolioId}/delete`,
  PORTFOLIO_DESCRIPTION: (portfolioId: string) =>
    `/portfolios/${portfolioId}/description`,
  PORTFOLIO_ALLOCATIONS: (portfolioId: string) =>
    `/portfolios/${portfolioId}/allocations`,
  PORTFOLIO_OVERVIEW: (portfolioId: string) =>
    `/portfolios/${portfolioId}/overview`,
  PORTFOLIO_STRUCTURE: (portfolioId: string) =>
    `/portfolios/${portfolioId}/structure`,
  PORTFOLIO_PERFORMANCE: (portfolioId: string) =>
    `/portfolios/${portfolioId}/performance`,
  PORTFOLIO_RISK: (portfolioId: string) => `/portfolios/${portfolioId}/risk`,
  PORTFOLIO_CORRELATION: (portfolioId: string) =>
    `/portfolios/${portfolioId}/correlation`,
  PORTFOLIO_HISTORY: (portfolioId: string) =>
    `/portfolios/${portfolioId}/history`,
  PORTFOLIO_REPORTS: (portfolioId: string) =>
    `/portfolios/${portfolioId}/reports`,
  PORTFOLIO_NOTIFICATIONS: (portfolioId: string) =>
    `/portfolios/${portfolioId}/notifications`,
  // User
  USER_HOME: "/users/portfolios",
  USER: (userId: string) => `/users/${userId}`,
  USER_BIO: (userId: string) => `/users/${userId}/bio`,
  USER_PORTFOLIOS: (userId: string) => `/users/${userId}/portfolios`,
  USER_NOTIFICATIONS: (userId: string) => `/users/${userId}/notifications`,
  USER_SUBSCRIBERS: (userId: string) => `/users/${userId}/subscribers`,
  USER_SUBSCRIPTIONS: (userId: string) => `/users/${userId}/subscriptions`,

  USER_ALERTS: (userId: string) => `/users/${userId}/alerts`,
  USER_WATCHLIST: (userId: string) => `/users/${userId}/alerts`,

  // Social network
  INBOX: "/inbox",
  INBOX_CHAT: (channelId: string, params?: string) =>
    `/inbox/${channelId}` + (params ? `?${params}` : ""),
  INBOX_CHAT_ROOM: (channelId: string, params?: string) =>
    `/inbox/${channelId}/room` + (params ? `?${params}` : ""),
  INBOX_CHAT_ROOM_STREAM: (channelId: string, params?: string) =>
    `/inbox/${channelId}/room/stream` + (params ? `?${params}` : ""),
  INBOX_CHAT_ROOM_WATCH: (channelId: string, params?: string) =>
    `/inbox/${channelId}/room/watch` + (params ? `?${params}` : ""),

  MACRO_ECONOMY: "/macro",

  // Subscriptions
  SUBSCRIPTIONS: "/subscriptions",
  SUBSCRIPTION: (subscriptionId: string) => `/subscriptions/${subscriptionId}`,

  FEEDBACK: "https://denomos.featurebase.app/",

  CONTACT: "/contact",
} as const;

export function getFullRouteUrl(route: string) {
  const base = BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
  const path = route.startsWith("/") ? route.slice(1) : route;

  return `${base}${path}`;
}

const BASE_URL =
  process.env.NEXT_PUBLIC_BASE_URL ||
  process.env.BASE_URL ||
  "http://localhost:3000";

export const APIRoutes = {
  ASSET_SEARCH: `${BASE_URL}/api/assets/search`,
  PORTFOLIOS: `${BASE_URL}/api/portfolios`,
  PORTFOLIOS_LEADERBOARD: `${BASE_URL}/api/portfolios/leaderboard`,
  PORTFOLIO: `${BASE_URL}/api/portfolios/`,
  PORTFOLIO_CREATE: `${BASE_URL}/api/portfolios/create`,

  JOB_WALLET_CREATE: `${BASE_URL}/api/jobs/wallets/create`,
  JOB_EXCHANGE_CREATE: `${BASE_URL}/api/jobs/exchanges/create`,
  JOB_WALLET_SYNC: `${BASE_URL}/api/jobs/wallets/sync`,
  JOB_EXCHANGE_SYNC: `${BASE_URL}/api/jobs/exchanges/sync`,
  PORTFOLIO_DISTRIBUTION: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/distribution/image` as const,
  PORTFOLIO_ALLOCATIONS: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/allocations/image` as const,
  PORTFOLIO_GLOBAL_STATS: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/global-stats/image` as const,
  PORTFOLIO_RANK: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/rank/image` as const,
  PORTFOLIO_7DAYS_CHART: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/7-days-chart` as const,
  PORTFOLIO_ASSET: (id: string, assetName: string) =>
    `${BASE_URL}/api/portfolios/${id}/assets/${assetName}` as const,
  PORTFOLIO_DRAFT: `${BASE_URL}/api/portfolios/draft`,
  PORTFOLIO_EDIT_DRAFT: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/draft` as const,
  PORTFOLIO_BALANCE: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/balance` as const,
  PORTFOLIO_HISTORICAL_VALUE: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/historical-value` as const,
  PORTFOLIO_VALUES: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/value` as const,
  PORTFOLIO_PERFORMANCE: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/performance` as const,
  PORTFOLIO_METRICS: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/metrics` as const,
  PORTFOLIO_PCT_CHANGE: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/pct-change` as const,
  PORTFOLIO_SHARP_RATIO: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/sharp-ratio` as const,
  PORTFOLIO_CAGR: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/cagr` as const,
  PORTFOLIO_WATCHLIST: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/watchlist` as const,
  PORTFOLIO_COPY: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/copy` as const,
  PORTFOLIO_SEARCH: `${BASE_URL}/api/portfolios/search`,
  PORTFOLIO_IMAGE: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/image` as const,
  PORTFOLIO_NOTIFICATIONS: (id: string) =>
    `${BASE_URL}/api/portfolios/${id}/notifications` as const,

  PORTFOLIOS_COUNT: `${BASE_URL}/api/portfolios/count`,

  PROXY: `${process.env.NEXT_PUBLIC_PROXY_URL}/api/proxy` as const,

  USER_ME: `${BASE_URL}/api/users/me`,
  USER_CHATS: `${BASE_URL}/api/users/me/chats`,
  USER_SUBSCRIBERS: `${BASE_URL}/api/users/me/subscribers`,
  USER_SUBSCRIBED: `${BASE_URL}/api/users/me/subscribed`,
  USER_POSTS: (id: string) => `${BASE_URL}/api/users/${id}/posts` as const,
  USER_UPDATE: `${BASE_URL}/api/users/`,
  USER_WATCHLIST: `${BASE_URL}/api/users/watchlist`,
  USER_PORTFOLIOS: (id: string) =>
    `${BASE_URL}/api/users/${id}/portfolios` as const,
  USER_NOTIFICATIONS: (id: string) =>
    `${BASE_URL}/api/users/${id}/notifications` as const,
  USER_NOTIFICATIONS_UNREAD: `${BASE_URL}/api/users/notifications/unread`,
  USERS_SEARCH: `${BASE_URL}/api/users/search`,
  USER_FOLLOWERS: `${BASE_URL}/api/users/followers`,
  USER_FOLLOWING: `${BASE_URL}/api/users/following`,

  USER_FOLLOWS: (id: string, followingId: string) =>
    `${BASE_URL}/api/users/${id}/follows/${followingId}` as const,

  ENLIST: `${BASE_URL}/api/enlist`,

  ASSETS: `${BASE_URL}/api/assets`,
  ASSET: `${BASE_URL}/api/assets/`,
  ASSET_CREATE: `${BASE_URL}/api/assets/create`,
  ASSET_PRICES: `${BASE_URL}/api/assets/prices`,
  ASSET_PRICE: (assetId: string) =>
    `${BASE_URL}/api/assets/${assetId}/price` as const,
  ASSETS_GLOBAL_STATS: `${BASE_URL}/api/assets/global-stats`,

  SIGN_IN: "/api/auth/signin",
  SIGN_OUT: "/api/auth/signout",

  PUSH_VAPID_KEY: `${BASE_URL}/api/push/vapid-key`,
  PUSH_SUBSCRIBE: `${BASE_URL}/api/push/subscribe`,
  PUSH_UNSUBSCRIBE: `${BASE_URL}/api/push/unsubscribe`,

  SOCKET: "/api/socket/io",
  SOCKET_CHAT_ROOMS: `${BASE_URL}/api/socket/rooms`,
  SOCKET_CHAT_MESSAGES: `${BASE_URL}/api/socket/messages`,
  SOCKET_CHATS_PRESENCE: `${BASE_URL}/api/socket/chats/presence`,
  SOCKET_TRANSACTION_STATUS: `${BASE_URL}/api/socket/transactions`,

  POSTS: `${BASE_URL}/api/posts`,
  POSTS_RSS: (params?: string) =>
    `${BASE_URL}/api/posts/rss` + (params ? `?${params}` : ""),
  POST_CREATE: `${BASE_URL}/api/posts/create`,
  POST_REACT: (postId: string) =>
    `${BASE_URL}/api/posts/${postId}/reaction` as const,
  POST_IMAGE_UPLOAD: `${BASE_URL}/api/posts/image/upload`,
  POST: (postId: string) => `${BASE_URL}/api/posts/${postId}` as const,
  POST_DRAFT: `${BASE_URL}/api/posts/draft`,
  POST_COMMENTS: (postId: string) =>
    `${BASE_URL}/api/posts/${postId}/comments` as const,
  STREAM: `${BASE_URL}/api/stream`,
  CHAT_CREATE: `${BASE_URL}/api/chats/create`,
  CHAT: (chatId: string) => `${BASE_URL}/api/chats/${chatId}` as const,
  CHAT_ROOM: (chatId: string, params?: string) =>
    `${BASE_URL}/api/chats/${chatId}/room` + (params ? `?${params}` : ""),
  CHATS: `${BASE_URL}/api/chats`,
  CHATS_SEARCH: `${BASE_URL}/api/chats/search`,
  CHAT_MESSAGES: (chatId: string) => `${BASE_URL}/api/chats/${chatId}/messages`,
  CHAT_MESSAGE_REPLIES: (chatId: string, messageId: string) =>
    `${BASE_URL}/api/chats/${chatId}/messages/${messageId}/replies` as const,

  EXCHANGE_RATE: `${BASE_URL}/api/exchange/rate`,
  SUBSCRIPTION_CURRENCIES: `${BASE_URL}/api/subscriptions/currencies`,

  JOB_PROGRESS: `${BASE_URL}/api/progress`,

  REVALIDATE: `${BASE_URL}/api/revalidate`,
} as const;

const CDN_URL = process.env.NEXT_PUBLIC_CDN_URL;
export const CDNRoutes = {
  assetThumbs: `${CDN_URL}/thumb`,
  attribution: (path: string) =>
    `${CDN_URL}/assets/images/attribution/${path}` as const,
  assetsLarge: `${CDN_URL}/large`,
  assets: `${CDN_URL}/assets`,
  blogArticle: (name: string) =>
    `${CDNRoutes.assets}/blog/articles/${name}` as const,
  blockchainLogo: (path: string) =>
    `${CDNRoutes.assets}/images/blockchains/${path}` as const,
  exchangeLogo: (path: string) =>
    `${CDNRoutes.assets}/images/exchanges/${path}` as const,
  landingPage: (path: string) =>
    `${CDNRoutes.assets}/images/landing-page/${path}` as const,
  landingPageFunny: (path: string) =>
    `${CDNRoutes.assets}/images/landing-page/funny/${path}` as const,

  flag: (path: string) => `${CDN_URL}/assets/images/flags/${path}` as const,
  openGraph: (path: string) =>
    `${CDNRoutes.assets}/images/open-graph/${path}?v=${process.env.NEXT_PUBLIC_APP_VERSION}&ts=${Date.now()}` as const, // https://developer.x.com/en/docs/x-for-websites/cards/guides/troubleshooting-cards#outdated
  public: (path: string) => `${CDNRoutes.assets}/public/${path}` as const,
  portfolios: (portfolioId: string) =>
    `${CDNRoutes.assets}/portfolios/${portfolioId}` as const,
  rank: (rank: string) =>
    `${CDNRoutes.assets}/portfolio-war-ranks/${rank}` as const,
  users: (userId: string) => `${CDNRoutes.assets}/users/${userId}` as const,
  team: (path: string) => `${CDNRoutes.assets}/images/team/${path}` as const,
} as const;

const apiResponseSuccessSchema = z.object({
  success: z.literal(true),
  data: z.any(),
});

const apiResponseErrorSchema = z.object({
  success: z.literal(false),
  message: z.string(),
});

export const apiResponseSchema = z.union([
  apiResponseSuccessSchema,
  apiResponseErrorSchema,
]);

export function parseApiResponse(response: any) {
  return apiResponseSchema.parse(response);
}

export const SocketEvents = {
  CHAT_MESSAGE: (channelId: string) => `chat:${channelId}:message` as const,
  CHAT_ROOM: (roomId: string) => `chat:room:${roomId}` as const,
  CHAT_CHANNEL: (channelId: string) => `chat:${channelId}` as const,
  CHAT_CHANNEL_PRESENCE: (channelId: string) =>
    `chat:${channelId}:presence` as const,
  CHAT_MESSAGE_UPDATE: (channelId: string) =>
    `chat:${channelId}:message:update` as const,

  POST_DRAFT: "post:draft",

  TRANSACTION_STATUS: (payerId: string) =>
    `transaction:${payerId}:status` as const,
};

export const NextTags = {
  USER_NOTIFICATIONS: "user-notifications",
  PORTFOLIO_NOTIFICATIONS: "portfolio-notifications",
  PORTFOLIO: (portfolioId: string) => `portfolio-${portfolioId}` as const,
  PORTFOLIO_PERFORMANCE_DATA: (portfolioId: string) =>
    `portfolio-${portfolioId}-performance-data` as const,
  PORTFOLIO_PERFORMANCE_DRAWDOWN: (portfolioId: string) =>
    `portfolio-${portfolioId}-performance-drawdown` as const,
  PORTFOLIO_OVERVIEW_DATA: (portfolioId: string) =>
    `portfolio-${portfolioId}-overview-data` as const,
  PORTFOLIO_STRUCTURE_DATA: (portfolioId: string) =>
    `portfolio-${portfolioId}-structure-data` as const,
  PORTFOLIO_RISK_DATA: (portfolioId: string) =>
    `portfolio-${portfolioId}-risk-data` as const,
  PORTFOLIO_ASSETS_DATA: (portfolioId: string) =>
    `portfolio-${portfolioId}-assets-data` as const,
  PORTFOLIO_FIRST_CREATED: "portfolio-first-created",
  USER_SESSION: "user-session",
  USER_AFFILIATE: "user-affiliate",
  USER_SUBSCRIBED: "user-subscribed",
  USER_PORTFOLIOS: (userId: string) => `user-${userId}-portfolios` as const,
} as const;
